/**
 * MY TRADES (FR-U-TXN-008, FR-U-WAL-008). The way back to a locked asset.
 *
 * Without this screen the trade room was reachable only from a fresh take or a saved link, so
 * somebody who closed the tab on a trade holding their money had no route to the page that explains
 * why it is held. FR-U-WAL-008 asks for exactly that path: see the lock, click through to the cause.
 *
 * ## What "waiting for you" means here
 *
 * It is computed from the state and the viewer's role together, both of which come from the server.
 * A row that says "waiting for you" when it is not, or stays quiet when it is, is worse than no
 * label: on the manual path the seller confirming is the only thing that releases the buyer's
 * purchase, and a seller who thinks they have nothing to do is the reason a trade times out into a
 * dispute neither party wanted.
 */

import { Link } from 'react-router-dom';
import { api, type P2PTrade } from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';

interface Trades {
  trades: P2PTrade[];
}

const STATE_LABEL: Record<string, string> = {
  locked_in_escrow: 'Locked in escrow',
  awaiting_payment: 'Awaiting payment',
  payment_marked: 'Buyer says they have paid',
  awaiting_release: 'Awaiting release',
  released: 'Released',
  under_dispute: 'In dispute',
  cancelled: 'Cancelled',
};

/** Whether this trade is waiting on the person looking at it. Role and state, never state alone. */
function waitingForYou(t: P2PTrade): boolean {
  if (t.viewer_role === 'buyer') {
    // On the automatic path the platform is watching for the payment, so there is nothing to do.
    return t.state === 'awaiting_payment' && !t.settles_automatically;
  }
  return t.state === 'payment_marked';
}

export default function P2pTrades() {
  const { data, loading, error, reload } = useResource<Trades>(() => api.p2pTrades(), []);

  if (error) return <ErrorState body="We could not load your trades." onRetry={reload} />;

  const trades = data?.trades ?? [];

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-page-title text-ink">My trades</h1>
          <p className="mt-0.5 text-sm text-ink-muted">
            Open trades first. An asset in escrow stays there until the trade is settled or a
            dispute is decided.
          </p>
        </div>
        <Link
          to="/p2p"
          className="shrink-0 rounded-field border border-border bg-surface px-3.5 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
        >
          Offers
        </Link>
      </header>

      {loading ? (
        <LoadingCard lines={4} />
      ) : trades.length === 0 ? (
        <div className="rounded-card border border-border bg-surface p-6 text-center shadow-card">
          <p className="text-sm font-medium text-ink">No trades yet</p>
          <p className="mt-1 text-sm text-ink-muted">
            Take an offer from the book, or buy from VASP at today&rsquo;s rate.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {trades.map((t) => {
            const yours = waitingForYou(t);
            const open = t.state !== 'released' && t.state !== 'cancelled';
            return (
              <li key={t.id}>
                <Link
                  to={`/p2p/trades/${t.id}`}
                  className="block rounded-card border border-border bg-surface p-4 shadow-card transition hover:bg-surface-sunken"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="tabular text-sm font-semibold text-ink">
                        {t.amount} {t.asset}
                      </p>
                      <p className="mt-0.5 text-xs capitalize text-ink-subtle">
                        You are the {t.viewer_role}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-pill px-2 py-0.5 text-micro font-medium ${
                        t.state === 'under_dispute'
                          ? 'bg-warning/10 text-warning'
                          : t.state === 'released'
                            ? 'bg-success/10 text-success'
                            : 'bg-surface-sunken text-ink-muted'
                      }`}
                    >
                      {STATE_LABEL[t.state] ?? t.state}
                    </span>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3">
                    <p className="text-xs text-ink-muted">
                      {t.settles_automatically ? 'Settles automatically' : 'Seller confirms'}
                    </p>
                    {yours && (
                      <span className="rounded-pill bg-primary/10 px-2 py-0.5 text-micro font-semibold text-primary">
                        Waiting for you
                      </span>
                    )}
                  </div>

                  {/*
                    Said on the row rather than only inside the room. Somebody scanning this list is
                    usually looking for where their money went, and the answer is on the row.
                  */}
                  {open && t.escrow_held && (
                    <p className="mt-2 text-xs text-ink-subtle">
                      {t.viewer_role === 'seller' ? 'Your' : 'The seller’s'} {t.asset} is locked in
                      escrow.
                    </p>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
